import { changePlanStatusList, activePlanStatusList, restartPlanStatusList } from 'Utils/Constants';
import { checkSubscription } from 'Utils/Subscriptions';
import { getSubscription, getSubscriptionStatus, getChurnedRestartableSubscription } from 'Api/AccountApi';
import { setPaymentTypeProperty, setSubscriptionStateProperty } from 'Utils/Analytics/Amplitude';
import {
  CLEAR_SUBSCRIPTION,
  SUBSCRIPTION_ERROR,
  SUBSCRIPTION_REQUEST,
  SUBSCRIPTION_STATUS_REQUEST,
  SUBSCRIPTION_SUCCESS,
  SUBSCRIPTION_STATUS_SUCCESS,
} from '../Types';

// action creators
export const subscriptionsRequest = () => ({ type: SUBSCRIPTION_REQUEST });
export const subscriptionStatusRequest = () => ({ type: SUBSCRIPTION_STATUS_REQUEST });

export const subscriptionsSuccess = (payload) => ({ type: SUBSCRIPTION_SUCCESS, payload });
export const subscriptionsError = (payload) => ({ type: SUBSCRIPTION_ERROR, payload });
export const subscriptionStatusSuccess = (payload) => ({ type: SUBSCRIPTION_STATUS_SUCCESS, payload });
export const clearSubscription = () => ({ type: CLEAR_SUBSCRIPTION });

// helper function
const checkPurchaseType = (paymentMethod = '', partner = '') => {
  switch (true) {
    case paymentMethod === 'Credit/Debit Card':
    case paymentMethod === 'PayPal Account':
    case paymentMethod === 'ApplePay':
      return 'web';
    case paymentMethod === 'Google Wallet':
    case paymentMethod === 'Google Payment':
      return 'android';
    case paymentMethod === 'App Store Billing':
      return 'ios';
    case paymentMethod === 'Roku Payment':
      return 'roku';
    case paymentMethod === 'Fox Partner Payment' && partner === 'Samsung Checkout':
      return 'samsung';
    //Some early bango accounts don't have a partner value
    case paymentMethod === 'Fox Partner Payment' && (!partner || partner === 'Verizon'):
      return 'bango';
    case paymentMethod === 'Coupon':
      return 'coupon';
    default:
      return paymentMethod;
  }
};

// action thunks
export const fetchSubscriptions = () => {
  return async (dispatch, getState) => {
    const {
      appConfig: {
        defaultConfig: { productSkus },
      },
    } = getState();

    dispatch(subscriptionsRequest());
    try {
      const subs = await getSubscription(productSkus);
      const { error = '' } = subs;
      if (error) {
        throw new Error('Error: Cannot fetch user subscription');
      } else {
        const sub = checkSubscription(subs);
        dispatch(subscriptionsSuccess(sub));
      }
    } catch (error) {
      dispatch(subscriptionsError(error.message));
      console.error(error);
    }
  };
};

export const fetchSubscriptionStatus = () => async (dispatch) => {
  dispatch(subscriptionStatusRequest());
  try {
    const data = await getSubscriptionStatus();

    if (!data) {
      throw new Error('Error: Cannot fetch user subscription status');
    } else {
      const [currentSubscription = {}] = data;
      if (currentSubscription && currentSubscription.status) {
        const serviceId = currentSubscription.serviceID;
        const activeSubscriptionStatus = currentSubscription.status || '';
        const activeSubscriptionPlanName = currentSubscription.displayName || '';
        const activeSubscriptionServiceName = currentSubscription.serviceName || '';
        const activePaymentMethod = currentSubscription.paymentMethod || '';
        const activeSubscriptionId = serviceId || '';
        const isChangePlanAvailable = changePlanStatusList.includes(activeSubscriptionStatus);
        const hasActiveSubscription = activePlanStatusList.includes(activeSubscriptionStatus);
        const isPendingCancel = serviceId ? restartPlanStatusList.includes(activeSubscriptionStatus) : false;
        const isExistingPlanUser = activeSubscriptionStatus === 'Existing';
        const isTrialistUser = activeSubscriptionStatus.includes('Trialist');
        const isNewUser = activeSubscriptionStatus === 'New'; // New user is w/o subscription, churned user have different status.
        const isPendingPause = activeSubscriptionStatus === 'Pending Pause';
        const isPaused = activeSubscriptionStatus === 'Pause';
        const isChurnedUser = activeSubscriptionStatus.includes('Churned');
        const pauseStartDate = currentSubscription.pauseStartDate || 0;
        const pauseEndDate = currentSubscription.pauseEndDate || 0;
        const purchaseType = checkPurchaseType(activePaymentMethod, currentSubscription.partner);
        const isIAPUser = ['ios', 'android', 'roku', 'bango'].includes(purchaseType);
        const activeSubscriptionTier = currentSubscription.tier || 0;
        // This will need to be changed if we ever start receiving more than one promotional segment
        const accountPromotionSegment = currentSubscription.segments ? currentSubscription.segments[0] : {};

        let isChurnedRestartAvailable = false;
        let restartChurnedUserDetails = {};

        if (hasActiveSubscription || isChurnedUser) {
          setPaymentTypeProperty(currentSubscription.paymentMethod);
          setSubscriptionStateProperty(currentSubscription.status);
        }

        if (isChurnedUser) {
          try {
            const { isEligibleForRestart, ...churnedDetails } = await getChurnedRestartableSubscription();
            isChurnedRestartAvailable = isEligibleForRestart;
            restartChurnedUserDetails = churnedDetails;
          } catch (error) {
            console.error(error);
          }
        }

        dispatch(
          subscriptionStatusSuccess({
            activeSubscriptionId,
            activePaymentMethod,
            activeSubscriptionPlanName,
            activeSubscriptionServiceName,
            activeSubscriptionStatus,
            hasActiveSubscription,
            isChangePlanAvailable,
            isChurnedRestartAvailable,
            isChurnedUser,
            isExistingPlanUser,
            isNewUser,
            isIAPUser,
            isPaused,
            isPendingPause,
            isPendingCancel,
            isTrialistUser,
            pauseEndDate,
            pauseStartDate,
            purchaseType,
            restartChurnedUserDetails,
            activeSubscriptionTier,
            accountPromotionSegment,
          })
        );
      }
      return currentSubscription;
    }
  } catch (error) {
    console.error(error);
    return {};
  }
};
